/**
 * Shared helper for chart data-quality tooltips.
 *
 * When a fiscal year has no value for a metric, the chart still keeps the year on
 * the axis (so the gap is visible and hoverable) instead of dropping it. This
 * guarantees every empty year has SOMETHING to explain it on hover: the backend's
 * own note (e.g. MARKET_CAP_UNDERIVABLE — "Not calculated, not listed for this
 * period") when present, or a generic fallback when the year simply has no data.
 */
import type { DataNote } from '../../../types/financial';

/** Shown for an empty year that carries no explanatory note of its own. */
export const MISSING_DATA_NOTE: DataNote = {
  code: 'NO_DATA',
  severity: 'info',
  category: 'missing_data',
  title: 'No market valuation for this year',
  message:
    'This metric has no value for this fiscal year, so there is nothing to plot. ' +
    'Market-based metrics (market cap, enterprise value, EV/EBITDA, WACC) need a traded ' +
    'year-end share price; when none exists for the period — most often a year before the ' +
    'company was listed — they are left blank rather than estimated. Any operating figures ' +
    'for the year (revenue, EBITDA, ROIC) are unaffected.',
  fields_affected: [],
};

/**
 * Shown where a market-derived metric is null BY DESIGN: the company has no
 * traded market price (typically a PDF-ingested annual report). Market cap is
 * deliberately never faked; where possible the engine computes WACC/EV on
 * clearly-labelled book-value assumptions instead (see the Assumptions panel),
 * and anything it could not compute it leaves null. Null here must never be
 * rendered as 0 and never recomputed client-side — this note is the
 * explanation the blank carries.
 */
export const NO_MARKET_PRICE_NOTE: DataNote = {
  code: 'NO_MARKET_PRICE',
  severity: 'info',
  category: 'market_data',
  title: 'Not calculated — no market price',
  message:
    'This metric needs a traded share price, and none exists for this company or period. ' +
    'The calculation engine never fakes a market value: market cap is left blank, and ' +
    'metrics it can still derive (e.g. WACC on book-value weights) are computed on ' +
    'clearly-labelled assumptions instead — see the Assumptions panel. This blank is ' +
    'deliberate, not missing data. Operating figures (revenue, EBITDA, ROIC) are unaffected.',
  fields_affected: [],
};

/**
 * Notes to surface for a year in a chart tooltip.
 *
 * - value present  -> the year's own stored notes (may be empty).
 * - value missing + has own notes -> those notes explain the gap.
 * - value missing + no notes -> the generic MISSING_DATA_NOTE, so an empty
 *   column/point never hovers to an empty tooltip.
 */
export function tooltipNotesForYear(
  notes: DataNote[] | undefined,
  hasValue: boolean,
): DataNote[] {
  const list = notes ?? [];
  if (!hasValue && list.length === 0) return [MISSING_DATA_NOTE];
  return list;
}

/**
 * Whether a year's value for the given metric field(s) was estimated or
 * restated — i.e. it carries a non-informational data note that either names
 * one of `fields` or is generic (no specific fields). Provenance-only ('info')
 * notes never count: they record where a value came from without calling it
 * into question.
 *
 * Bars for such years are drawn at ESTIMATED_FILL_OPACITY so an estimated
 * figure reads as less certain than an observed one. This is field-aware on
 * purpose: an estimated year-end price flags the market-derived metrics
 * (market_cap / enterprise_value / ev_ebitda / wacc) but NOT the year's actual
 * reported operating figures (revenue, EBITDA, invested capital), so those bars
 * stay solid.
 */
export function isEstimatedValue(
  notes: DataNote[] | undefined,
  ...fields: string[]
): boolean {
  if (!notes || notes.length === 0) return false;
  return notes.some(
    (n) =>
      n.severity !== 'info' &&
      (n.fields_affected.length === 0 ||
        n.fields_affected.some((f) => fields.includes(f))),
  );
}

/** Opacity for a bar whose plotted value was estimated/adjusted (very light). */
export const ESTIMATED_FILL_OPACITY = 0.3;
